// Time formatting helpers — every user-facing timestamp in the
// dashboard / LIFF / scan flow renders in Taiwan time (Asia/Taipei,
// UTC+8). Operators are all in Taiwan; the device timezone is a
// red herring because (a) some staff travel with their phones,
// (b) the same shift schedule is the truth-of-record on the
// back-office side, and (c) audit / report exports need
// reproducible wall-clock times that don't drift with whoever's
// reading them. Centralising the formatter here means a single
// place to switch if we ever onboard a non-TW tenant.
//
// Inputs accept either:
//   - a Date instance
//   - a number (unix ms — `Date.parse()` / `new Date(ms).getTime()`)
//   - a string (ISO 8601 — passes through `new Date()` parsing)
// Anything else returns the literal `"—"` so a malformed timestamp
// doesn't crash the table row it's rendering inside.

const TZ = "Asia/Taipei";
const PLACEHOLDER = "—";

type Stamp = Date | number | string | null | undefined;

function toDate(v: Stamp): Date | null {
  if (v == null || v === "") return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

/** "2026-05-12 22:00" — date + time, second-precision optional. */
export function formatTaipeiDateTime(v: Stamp, opts: { withSeconds?: boolean } = {}): string {
  const d = toDate(v);
  if (!d) return PLACEHOLDER;
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: opts.withSeconds ? "2-digit" : undefined,
    hour12: false,
  }).format(d).replace(/\//g, "-");
}

/** "2026-05-12" — date only, no time. */
export function formatTaipeiDate(v: Stamp): string {
  const d = toDate(v);
  if (!d) return PLACEHOLDER;
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d).replace(/\//g, "-");
}

/** "22:00" — time of day only. */
export function formatTaipeiTime(v: Stamp, opts: { withSeconds?: boolean } = {}): string {
  const d = toDate(v);
  if (!d) return PLACEHOLDER;
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
    second: opts.withSeconds ? "2-digit" : undefined,
    hour12: false,
  }).format(d);
}

/**
 * Coarse「N 秒/分/小時/天前」 label. Used in lists where exact
 * wall-clock is overkill — last-seen on devices, recent activity, etc.
 * Returns `"—"` for null / invalid input.
 */
export function formatTaipeiAgo(v: Stamp): string {
  const d = toDate(v);
  if (!d) return PLACEHOLDER;
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 60) return `${sec} 秒前`;
  if (sec < 3600) return `${Math.floor(sec / 60)} 分鐘前`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} 小時前`;
  return `${Math.floor(sec / 86400)} 天前`;
}

/**
 * Short relative-day label + time when nearby; falls back to a
 * compact MM-DD HH:MM for anything further out. Used by the scan
 * wizard's "next occurrence" chip and a few similar spots where a
 * full timestamp is overkill.
 */
export function formatTaipeiRelative(v: Stamp): string {
  const d = toDate(v);
  if (!d) return PLACEHOLDER;
  // Date-arithmetic-without-tz is sketchy, so we compare the Taipei
  // calendar-date parts of both moments via the formatter. Slower
  // than Date.getDate() but timezone-correct.
  const parts = (date: Date): { y: string; m: string; d: string; hh: string; mm: string } => {
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: TZ,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(date);
    const get = (t: string): string => fmt.find((p) => p.type === t)?.value ?? "00";
    return { y: get("year"), m: get("month"), d: get("day"), hh: get("hour"), mm: get("minute") };
  };
  const now = parts(new Date());
  const tgt = parts(d);
  const sameDay = now.y === tgt.y && now.m === tgt.m && now.d === tgt.d;
  // Next-day check via UTC ms difference is fine because both are
  // anchored to the same TZ via the parts comparison.
  const ymd = (p: { y: string; m: string; d: string }): number => Date.UTC(+p.y, +p.m - 1, +p.d);
  const isTomorrow = ymd(tgt) - ymd(now) === 86_400_000;
  if (sameDay)    return `今天 ${tgt.hh}:${tgt.mm}`;
  if (isTomorrow) return `明天 ${tgt.hh}:${tgt.mm}`;
  return `${tgt.m}-${tgt.d} ${tgt.hh}:${tgt.mm}`;
}
